import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';

import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { LanguageModule } from '@language';
import { ApiQuery } from '../../../core/api/entities/api-query.entity';
import { AutocompleteRequest } from '../../../core/autocomplete/entities/autocomplete-request.entity';
import { DialogConfig } from '../../../dynamic-dialog/entities/dialog-config.entity';
import { DialogRef } from '../../../dynamic-dialog/entities/dialog-ref.class';
import { ShFacetPlaceHolderComponent } from '../../../elements/components/facet-placeholder/facet-placeholder.component';
import { ShFacet } from '../../../elements/components/facet/entities/facet.entity';
import { ShFacetComponent } from '../../../elements/components/facet/facet.component';
import { ShNotificationComponent } from '../../../elements/components/notification/notification.component';
import { ShFormTextInputComponent } from '../../../form-controls/components/form-text/main/form-text.component';
import { COMPLETABLE_TASK_FACET } from '../../constants/virtual-facets.constants';
import { ShTaskAutocompleteDataService } from '../../data-services/task-autocomplete.data.service';
import { ShTaskFacetDataService } from '../../data-services/task-facet.data-service';
import { FacetValueConstraint } from '../../entities/facet-request.entity';
import { ProfileFacetSearchDialogOutput, TaskFacetSearchDialogConfig } from '../../entities/facet-search-dialog.entity';
import { ShProfileFacetSearchDialogComponent } from '../profile-facet-search-dialog/profile-facet-search-dialog.component';

@Component({
    standalone: true,
    selector: 'sh-task-facet-search-dialog',
    templateUrl: '../profile-facet-search-dialog/profile-facet-search-dialog.component.html',
    styleUrls: ['../profile-facet-search-dialog/profile-facet-search-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        LanguageModule,
        ShFacetComponent,
        ShNotificationComponent,
        ShFacetPlaceHolderComponent,
        ShFormTextInputComponent
    ],
    providers: []
})
export class ShTaskFacetSearchDialogComponent extends ShProfileFacetSearchDialogComponent implements OnInit, OnDestroy {
    public readonly ONLY_COMPLETABLE: boolean = false;

    // Injectors
    protected override readonly facetsService: ShTaskFacetDataService = inject(ShTaskFacetDataService);
    protected override readonly autocompleteService: ShTaskAutocompleteDataService =
        inject(ShTaskAutocompleteDataService);

    constructor(
        protected readonly formBuilder: FormBuilder,
        protected readonly config: DialogConfig<TaskFacetSearchDialogConfig>,
        protected readonly dialog: DialogRef<ProfileFacetSearchDialogOutput>
    ) {
        super(formBuilder, config, dialog);

        if (this.config.data) {
            this.ONLY_COMPLETABLE = this.config.data.onlyCompletable;
        }
    }

    // #region Overrides
    protected override getFacetPage(pageIndex: number): Observable<ShFacet> {
        const constraint: FacetValueConstraint = {
            StartIndex: pageIndex * this.FACET_FIELD.DefaultNumberOfValues
        };

        return this.facetsService
            .getProfileFieldFacet(
                this.apiQuery,
                this.FACET_FIELD,
                constraint,
                this.INCLUDE_ACTIVE_VALUES,
                this.ONLY_COMPLETABLE
            )
            .pipe(take(1));
    }

    protected override getAutocompleteRequest(valueFilter: string): AutocompleteRequest {
        const autocompleteRequest = super.getAutocompleteRequest(valueFilter);
        autocompleteRequest.Search.OnlyCompletable = this.ONLY_COMPLETABLE;

        return autocompleteRequest;
    }

    protected override get apiQuery(): ApiQuery {
        const apiQuery = super.apiQuery;
        apiQuery.deleteFieldIfPresent(COMPLETABLE_TASK_FACET);
        return apiQuery;
    }
    // #endregion
}
